import type { GaVueComponent } from "@/common/vueUtils";
import type { FormFilter, FormFilterName } from "@newgenerated/shared/schema";
import { GaChip } from "@/components/general/GaChip";
import { assertIsDefined } from "@utils/assertion";
import type { DeepReadonly } from "vue";
import { GaFormFieldMultiSelect } from "@/components/form/GaFormFieldMultiSelect";
import type { Store } from "@/common/storeUtils";
import type { FormFilterExtended } from "@/components/sorting-filtering-paging/ContentSortingFiltering";

function Header(props: { headerId: string; collapseId: string; filter: DeepReadonly<FormFilter>; onClickHeader: (filterName: FormFilterName) => void; isOpen: boolean }): GaVueComponent {
  return (
    <h5 class="filtering-dropdown__header" id={props.headerId} onClick={() => props.onClickHeader(props.filter.identifier)}>
      <button class="filtering-dropdown__header-button" type="button" aria-expanded={props.isOpen} aria-controls={props.collapseId}>
        <span>{props.filter.label}</span>
        <i class="ico-chevron-down" aria-hidden="true"></i>
      </button>
    </h5>
  );
}

function FilterImplementation(props: { filter: Store<FormFilterExtended> }): GaVueComponent {
  const filter = props.filter.get();
  const filterId = filter.name + "-filter-" + window.crypto.randomUUID().substring(0, 7);
  switch (filter.type) {
    case "MULTICHECKBOX":
      return (
        <ul class="list-unstyled filtering-dropdown__chip-list">
          {filter.options.map((filterOption) => {
            const active = filter.activeValues.includes(filterOption.value);
            return (
              <li>
                <GaChip active={active} type="checkbox" changeHandler={() => props.filter.sub("activeValues").set(active ? filter.activeValues.filter((f) => f !== filterOption.value) : [...filter.activeValues, filterOption.value])}>
                  {filterOption.label}
                </GaChip>
              </li>
            );
          })}
        </ul>
      );
    case "CHECKBOX": {
      const active = filter.activeValues.at(0) === "true";
      return (
        <GaChip active={active} type="checkbox" changeHandler={() => props.filter.sub("activeValues").set([active ? "false" : "true"])}>
          {filter.options.at(0)?.label}
        </GaChip>
      );
    }
    case "MULTISELECT": {
      const unpackedProps = props.filter.sub("multiSelectProps").unpackNull();
      assertIsDefined(unpackedProps);
      return (
        <>
          <label for={filterId} class="visually-hidden">
            {filter.label}
          </label>
          <GaFormFieldMultiSelect
            options={[...unpackedProps.sub("options").get()]}
            selectedOptions={[...filter.activeValues]}
            onChange={(selection) => props.filter.sub("activeValues").set(selection)}
            searchTerm={unpackedProps.get().searchTerm}
            onSearchTermChange={(term) => unpackedProps.sub("searchTerm").set(term)}
            showSearch={unpackedProps.sub("showSearch").get()}
            toggleSearch={() => unpackedProps.sub("showSearch").set(!unpackedProps.sub("showSearch").get())}
          />
        </>
      );
    }
    case "RANGE": {
      const min = filter.options.at(0);
      const max = filter.options.at(1);
      assertIsDefined(min);
      assertIsDefined(max);
      const firstLimit = filter.activeValues.at(0);
      const secondLimit = filter.activeValues.at(1);
      const selectedLowerLimit = firstLimit ?? min.value;
      const selectedUpperLimit = firstLimit === undefined ? max.value : (secondLimit ?? firstLimit);
      return (
        <div class="input-group">
          <input
            type="number"
            class="form-control"
            aria-label={filter.label + ": Lower limit"}
            value={selectedLowerLimit}
            min={min.value}
            max={max.value}
            onInput={(event) => props.filter.sub("activeValues").set([(event.target as HTMLInputElement).value, secondLimit ?? max.value])}
          />
          <span class="input-group-text">&ndash;</span>
          <input
            type="number"
            class="form-control"
            aria-label={filter.label + ": Upper limit"}
            value={selectedUpperLimit}
            min={min.value}
            max={max.value}
            onInput={(event) => props.filter.sub("activeValues").set([firstLimit ?? min.value, (event.target as HTMLInputElement).value])}
          />
        </div>
      );
    }
    case "RANGE_MIN": {
      const activeValue = filter.activeValues.at(0);
      const minValue = filter.options.at(0);
      const maxValue = filter.options.at(1);
      assertIsDefined(activeValue);
      assertIsDefined(maxValue);
      assertIsDefined(minValue);
      const incrementingNumbers = Array.from({ length: Number(Number(maxValue.value) - Number(minValue.value) + 1) }, (_, i) => Number(minValue.value) + i);
      return (
        <>
          <label for={filterId} class="visually-hidden">
            {filter.label}
          </label>
          <select id={filterId} class="form-control form-select">
            {incrementingNumbers.map((index) => (
              <option value={index} selected={index === Number(activeValue)} onSelect={() => props.filter.sub("activeValues").set([index.toString()])}>
                {index}
                {index < Number(maxValue) ? "+" : ""}
              </option>
            ))}
          </select>
        </>
      );
    }
  }
}

export function Filter(props: { filter: Store<FormFilterExtended> }): GaVueComponent {
  const filter = props.filter.get();
  const uuid = window.crypto.randomUUID().substring(0, 7);
  const collapseId = filter.name + "filter-collapse" + uuid;
  const headerId = filter.name + "filter-collapse-header" + uuid;
  return (
    <section>
      <Header filter={filter} headerId={headerId} collapseId={collapseId} onClickHeader={() => props.filter.sub("isCollapsed").set(!props.filter.sub("isCollapsed").get())} isOpen={!props.filter.sub("isCollapsed").get()} />
      <div class={["filtering-dropdown__body", props.filter.sub("isCollapsed").get() ? "collapse" : ""]} id={collapseId}>
        <FilterImplementation filter={props.filter} />
      </div>
    </section>
  );
}
